import { createGlobalStyle, DefaultTheme, ThemeProvider } from 'styled-components';
import { ComponentSize } from '@mews-ui/theme';
import { dark, light } from '@mewssystems/design-tokens';
import { SemanticDecisions, TextPresets, ThemeOverrides } from '@optimus-web/local-tokens';
import { defaultTranslations, LocalizationProvider, LocalizationTexts } from '@optimus-web/localization';
import { createTheme, DENSE_DARK_THEME, DENSE_LIGHT_THEME } from '../theme';
import { ThemeVariant } from '../types';

export interface OptimusProviderProps {
    children: React.ReactNode;
    textPresets?: TextPresets;
    decisions?: SemanticDecisions;
    themeVariant?: ThemeVariant;
    overrides?: ThemeOverrides;
    localizationTexts?: LocalizationTexts;
    tokenOverrides?: Partial<typeof dark | typeof light>;
    componentSize?: Partial<ComponentSize>;
}

const GlobalStyles = createGlobalStyle`
    html {
        ${(props) => props.theme.typography.fontFamily && `font-family: ${props.theme.typography.fontFamily};`};
    }
    body {
        -moz-osx-font-smoothing: grayscale;
        -webkit-font-smoothing: antialiased;
    }
`;

const OptimusProvider = ({
    children,
    decisions,
    textPresets,
    themeVariant = ThemeVariant.Light,
    overrides,
    localizationTexts = defaultTranslations,
    tokenOverrides,
    componentSize,
    ...other
}: OptimusProviderProps) => {
    let baseTheme;

    switch (themeVariant) {
        case ThemeVariant.Light:
            baseTheme = DENSE_LIGHT_THEME;
            break;
        case ThemeVariant.Dark:
            baseTheme = DENSE_DARK_THEME;
            break;
        default:
            baseTheme = DENSE_LIGHT_THEME;
            break;
    }

    const finalTheme: DefaultTheme =
        decisions && (overrides || tokenOverrides) && textPresets
            ? createTheme({ decisions, themeVariant, textPresets, overrides, tokenOverrides, componentSize })
            : {
                  ...baseTheme,
                  designTokens: { ...baseTheme.designTokens, ...tokenOverrides },
                  settings: {
                      ...baseTheme.settings,
                      componentSize: { ...baseTheme.settings.componentSize, ...componentSize },
                  },
              };

    return (
        <ThemeProvider theme={finalTheme} {...other}>
            <LocalizationProvider localizationTexts={localizationTexts}>
                <>
                    <GlobalStyles />
                    {children}
                </>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default OptimusProvider;
